// moduleCatalog.js — Single source of truth for the role-permission editor's
// module catalog (the list of paths that can be granted to a role).
//
// Why this file exists: prior to this consolidation the same MODULE_GROUPS
// constant was duplicated VERBATIM in both `pages/UsersRoles.js` and
// `pages/HR.js`. Adding a new path required editing both files; forgetting
// one caused the silent "I granted access but the editor doesn't show it"
// bug. UsersRoles + HR now both import ROLE_EDITOR_GROUPS from here.
//
// SECTION TAXONOMY (2026-06-17 realign): sections now follow the SAP S/4HANA
// "Lines of Business" spine so a section maps to ONE job family — clean
// segregation-of-duties for granting access. Key principle: one module =
// one functional owner (no Finance-holds-HR, no Operations-holds-Procurement
// bleed). Manufacturing (PP) / Sourcing (MM) / Asset-Maintenance (PM) are
// split; Finance (FI/CO) / HR (HCM) / Analytics (BI) are split.
//
// Scope of this consolidation:
//   ✅ Frontend role-editor catalog (UsersRoles + HR) — unified here.
//   ✅ Backend ALL_MODULES (rolePermissions.js) — kept group-for-group in sync.
//   ⏳ navCatalog.js NAV_CATALOG (sidebar + mobile) — DELIBERATELY separate.
//      That tree is the daily-driver navigation UX (badges, founderOnly,
//      financeOnly, altPaths, sortAs pinning); the audit-oriented 22-section
//      taxonomy below is not appropriate for it. Align only on explicit ask.
//
// Data shape: array of { group, modules: [{ path, label, desc }] }.
// `desc` (2026-07-14) is the one-line "what does granting this unlock" used by
// the Module Access manager, the self-serve request card and the approval
// queue — write it for the APPROVER deciding whether to grant.

// Modules render alphabetically within each group (group order stays curated).
const sortKey = (label = '') => String(label).replace(/^[^A-Za-z0-9]+/, '').trim().toLocaleLowerCase('en-IN');
const sortModules = (groups) => groups.map((g) => ({
  ...g,
  modules: [...g.modules].sort((a, b) => sortKey(a.label).localeCompare(sortKey(b.label), 'en-IN', { numeric: true })),
}));

const ROLE_EDITOR_GROUPS_RAW = [
  // 1. Core / Workspace — Basis + Employee Self-Service. Everyone gets these.
  { group: 'Core / Workspace', modules: [
    { path: '/my-work',     label: 'My Work',            desc: 'Personal work queue — assigned actions, follow-ups and pending decisions.' },
    { path: '/',            label: 'Home / Dashboard',   desc: 'Personal home dashboard — greeting, tasks waiting, announcements.' },
    { path: '/tasks',       label: 'Tasks',              desc: 'Personal task inbox — assigned tasks, deadlines, completion flow.' },
    { path: '/attendance',  label: 'Attendance',         desc: 'Clock in/out, own attendance record and leave balances.' },
    { path: '/attendance/face-enrollment', label: 'Attendance — Face Enrollment (HR)', desc: 'HR tool to enroll employee face descriptors for kiosk face-scan attendance.' },
    { path: '/launchpad',   label: 'Launchpad',          desc: 'Full module directory — opens only what you already have access to. Universal (everyone has it); listed here for completeness.' },
  ]},
  // 2. Master Data — SAP MDG. Identifiers + golden records.
  { group: 'Master Data', modules: [
    { path: '/skus',         label: 'SKU Master',        desc: 'The product master — create/edit SKUs, codes, pricing, status.' },
    { path: '/vendors',      label: 'Vendor Master',     desc: 'Supplier master records — contacts, GST, bank, categories.' },
    { path: '/clients',      label: 'Client Master',     desc: 'B2B client master — companies we sell to, terms, contacts.' },
  ]},
  // 3. Sales & Orders — SAP SD + order control. Merged 2026-07-15 from the
  // former 'Sales & Distribution' + 'OMS / Order Control' + 'Salesforce'
  // groups (13 paths were duplicated verbatim across the first two).
  { group: 'Sales & Orders', modules: [
    { path: '/quotations',        label: 'Quotations (incl. CPQ Rules)', desc: 'Create and send quotes plus the configure-price-quote rules (discounts, tiers, guardrails). One grant covers both.' },
    { path: '/sales-orders',      label: 'Sales Orders',      desc: 'Create/confirm sales orders and drive them to dispatch.' },
    { path: '/sales-invoice',     label: 'Sales Invoice',     desc: 'Raise GST sales invoices (e-Invoice/IRN when configured).' },
  ]},
  // 4. CRM & Customer Service — SAP CS. Pre-sale relationship + post-sale support.
  // 5. E-Commerce & Channels — omnichannel SD.
  // 6. B2B / Trade Portal — buyer-facing SD-B2B.
  // 7. Marketing spine — split 2026-07 from one 32-module wall into five
  // focused heads (paths unchanged, so existing grants keep working).
  // 7b. Promotion — WhatsApp marketing broadcasts (separate module, own owner).
  // 8. R&D / PLM — Product Lifecycle Management. Formulation + artwork.
  { group: 'Stock & Materials', modules: [
    { path: '/rawmaterials',   label: 'Materials', desc: 'Material master — suppliers, units, prices.' },
    { path: '/batch-codes',    label: 'Batch Codes & COA', desc: 'Batch code allocation/decode plus Certificate of Analysis generation. One grant covers both.' },
    { path: '/rm-inwards',      label: 'Inwards / GRN (RM · Pkg · Labels)', desc: 'Receive raw materials, packaging and printed labels against POs (GRN) into stock. One grant covers all three inward pages.' },
    { path: '/ready-goods-inwards', label: 'Ready Goods Inwards', desc: 'Receive finished products bought ready-made from a manufacturer — with vendor invoice — straight into sellable stock.' },
    { path: '/stock-transfer',  label: 'Stock Transfer', desc: 'Move stock between locations/warehouses with a paper trail.' },
  
  ]},
  // 8b. Artwork & Labels — packaging artwork + label management.
  // 9. Regulatory & EHS — compliance dossiers + safety data.
  // 10. Quality Management — SAP QM. Inspection + CAPA + audits.
  { group: 'Quality Management', modules: [
    { path: '/qc',              label: 'Quality Control', desc: 'QC checks and pass/fail decisions on batches and inwards.' },
    { path: '/qms/incoming',    label: 'QMS (Incoming / In-Process / Release)', desc: 'Full QMS inspection chain — incoming, in-process and final release sign-off. One grant covers all three stages.' },
    { path: '/capa',            label: 'CAPA',         desc: 'Corrective/preventive actions — root cause to closure.' },
  ]},
  // 11. Manufacturing — SAP PP. Production planning + execution.
  // 12. Sourcing & Procurement — SAP MM/SRM. Buy-side + goods receipt (GRN).
  // 13. Warehouse & Inventory — SAP EWM/IM.
  { group: 'Warehouse & Inventory', modules: [
    { path: '/warehouse',           label: 'Warehouse Register (incl. Finished Goods)', desc: 'Stock register by SKU/location — closing quantities, reorder levels — plus the finished-goods sellable-stock view.' },
    { path: '/inventory-scan',      label: 'Inventory Scan', desc: 'Scan a barcode (RM / PM / MC / FRAG) — stock, consumption ledger, quick moves, label print.' },
  ]},
  // 14. Logistics & Dispatch — SAP LE. Outbound shipping.
  { group: 'Logistics & Dispatch', modules: [
    // Consolidated 2026-07-16: packing lists + shipping-label printing ride
    // the Dispatch grant (MODULE_GRANT_CLUSTERS) — one outbound-ops owner.
    { path: '/dispatch',            label: 'Dispatch (incl. Packing List & Label Print)', desc: 'Outbound dispatches — evidence photos, handover, POD — plus packing-list generation and shipping-label printing. One grant covers all three.' },
    { path: '/courier',             label: 'Courier (incl. DTDC & Blue Dart)', desc: 'Courier shipments — book, track, NDR handling, COD status. Covers the DTDC and Blue Dart boards too.' },
  ]},
  // 15. Asset & Maintenance — SAP PM/EAM.
  // 16. Finance & Controlling — SAP FI/CO. Ledgers, AR/AP, costing.
  // Consolidated 2026-07-15: the read-only ledger/report views (day book, GL,
  // AR aging, cash flow, client ledger, vendor payables, tally export, margin
  // simulator, profitability, ITR, cards, O2C, debit/credit notes, all
  // /finance/* cockpits) ride the single '/finance' umbrella grant
  // (FINANCE_UMBRELLA_VIEWS + /finance prefix). Narrow operational grants
  // stay separate below. All pages remain live.
  { group: 'Finance & Controlling', modules: [
    { path: '/invoices',              label: 'Finance — Invoices', desc: 'All invoices — status, dues, dunning. Customer money data.' },
    { path: '/payments',              label: 'Finance — Payments', desc: 'Payables/receivables records — record and reconcile payments.' },
    { path: '/gst-returns',           label: 'Finance — GST Returns', desc: 'GST return prep — GSTR data, reconciliation, filing status.' },
  ]},
  // 17. Human Resources — SAP HCM.
  { group: 'Human Resources', modules: [
    { path: '/payroll',               label: 'Payroll',      desc: 'Run payroll — salaries, deductions, payslips. Sensitive pay data.' },
    { path: '/salary',                label: 'Salary',       desc: 'Own salary/payslip view (self-service).' },
    { path: '/leave',                 label: 'Leave Management', desc: 'Leave requests, approvals and balances.' },
    { path: '/hr',                    label: 'HR & Team',    desc: 'Employee records, onboarding and HR settings. Sensitive people data.' },
  ]},
  // 18. Analytics & Intelligence — SAP BI/BW + market & brand intel.
  // 19. Investor Readiness — AONE-specific data room.
  // 20. Reseller Portal — external partner access.
  // 21. Governance, Risk & Compliance — SAP GRC. Controls + access gate.
  { group: 'Governance, Risk & Compliance', modules: [
    { path: '/security',       label: 'Security Center', desc: 'Security posture — sessions, audit log, anomalies. Admin-grade.' },
    { path: '/approval-rules', label: 'Approval Rules', desc: 'Configure what needs whose approval at what threshold.' },
  ]},
  // 22. System Administration — SAP Basis. Platform + automation.
  { group: 'System Administration', modules: [
    { path: '/users',                label: 'Users & Roles', desc: 'User accounts and role assignment. Admin-grade.' },
    { path: '/module-access',        label: 'Module Access', desc: 'Grant/revoke module access per user. THE access-control panel.' },
    { path: '/settings',             label: 'Settings (incl. Announcements)', desc: 'General application settings plus publishing dashboard announcements. One grant covers both.' },
  ]},
  // Vendor workbench — internal supplier collaboration pages.
  // Upload Hub — one page listing every bulk upload/import/export; each op
  // below is grantable on its own so e.g. Finance can run the COD remittance
  // import WITHOUT holding OMS. Keep in sync with backend
  // config/uploadHubCatalog.js (UPLOAD_HUB_GRANT_MODULES).
];

export const ROLE_EDITOR_GROUPS = sortModules(ROLE_EDITOR_GROUPS_RAW);

// Convenience flat list for callers that just need every grant-able path.
export const ROLE_EDITOR_PATHS = ROLE_EDITOR_GROUPS.flatMap(g => g.modules.map(m => m.path));

// Path → label lookup for callers that need to render a path nicely.
export const ROLE_EDITOR_LABELS = Object.fromEntries(
  ROLE_EDITOR_GROUPS.flatMap(g => g.modules.map(m => [m.path, m.label]))
);

// Path → one-line description ("what does granting this unlock"). Shown in the
// Module Access manager, the self-serve request card and approval queues so
// the approver knows what they are granting.
export const ROLE_EDITOR_DESCS = Object.fromEntries(
  ROLE_EDITOR_GROUPS.flatMap(g => g.modules.map(m => [m.path, m.desc || '']))
);
